import { useMutation } from "@apollo/client";
import { useState } from "react";

import DocumentationButton from "components/DocumentationButton";
import TextLink from "components/DocumentationSnippets/TextLink";
import FlashContext from "components/FlashMessages/FlashContext";
import MissingDataBanner from "components/MissingDataBanner";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import { useObserveForWarning } from "components/WarningContext/useObserveForWarning";
import FormLoading from "components/form/components/loading";
import { EmptystateHelmetColored } from "components/icons/generated";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import EmptyState from "ds/components/EmptyState";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { PolicyStackAttachment, PolicyType } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import Card from "ds/components/Card";

import NewStackFooter from "../Footer";
import { StackFormContext } from "../context";
import useErrorHandlerNewStack from "../useErrorHandlerNewStack";
import NewStackAutoAttachedPolicies from "./AutoAttachedPolicies";
import Documentation from "./Documentation";
import NewStackManuallyAttachedPolicies from "./ManuallyAttachedPolicies";
import { ATTACH_POLICY } from "./gql";
import { useAttachedPolicies } from "./hooks/useAttachedPolicies";
import { PolicyTypeOption, usePolicyTypeOptions } from "./hooks/usePolicyTypeOptions";

const NewStackAttachPolicies = () => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const { createdStackId } = useTypedContext(StackFormContext);

  const [policy, setPolicy] = useState<string | null>(null);
  const [policyType, setPolicyType] = useState<PolicyType | null>(null);

  useObserveForWarning(
    !!policy,
    <>
      You have not attached the <strong>selected policy</strong>. Do you want to continue without
      attaching it?
    </>
  );

  const {
    allPolicies,
    attachablePolicies,
    attachedPolicies,
    autoAttachedPolicies,
    manuallyAttachablePolicyTypes,
    loading,
    hasData,
    refetching,
    refetch,
    attachedError,
    attachableError,
  } = useAttachedPolicies();

  const attachablePolicyOptions = attachablePolicies.filter((policy) => policy.type === policyType);
  const policyTypeOptions = usePolicyTypeOptions(manuallyAttachablePolicyTypes);

  const [attachPolicy, { loading: isAttachingPolicy }] = useMutation<{
    policyAttach: PolicyStackAttachment;
  }>(ATTACH_POLICY, {
    refetchQueries: ["GetAttachedStackPolicies"],
    awaitRefetchQueries: true,
  });

  const handleAttach = () => {
    attachPolicy({ variables: { stackId: createdStackId, policyId: policy } })
      .then(({ data }) => {
        if (data?.policyAttach?.id) {
          setPolicy("");
          reportSuccess({ message: "Policy was attached" });
        }
      })
      .catch(onError);

    if (attachablePolicyOptions.length === 1) {
      setPolicyType(null);
    }
  };

  useErrorHandlerNewStack(attachedError);
  useErrorHandlerNewStack(attachableError);

  if (loading) {
    return <FormLoading />;
  }

  const hasNoPolicies = hasData && !allPolicies.length && !attachablePolicies.length;

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Attach policies (optional)"
          description={
            hasNoPolicies ? undefined : "Policies only affect stacks they’re attached to"
          }
        />
        {hasNoPolicies ? (
          <Box direction="column" gap="large">
            <Banner variant="info">
              You can only attach policies from the current space and parent spaces that you inherit
              from.
            </Banner>
            <Card variant="secondary">
              <EmptyState
                padding="large"
                icon={EmptystateHelmetColored}
                title="You do not have any policies yet"
                caption={
                  <>
                    Policies are used to control various decision points throughout your workflow
                    from how many approvals you need for a run, what happens when a PR is open, what
                    resources you can create, where you receive notifications and others. This gives
                    you full flexibility on owning your workflow and ensuring governance and
                    compliance through the integration & deployment lifecycles. <br /> Read more in
                    the Documentation.
                  </>
                }
              >
                <DocumentationButton to={getDocsUrl("/concepts/policy")} label="Documentation" />
              </EmptyState>
            </Card>
          </Box>
        ) : (
          <>
            <Banner variant="info">
              You can only attach policies from the current space and parent spaces that you inherit
              from.
            </Banner>

            {!hasData && (
              <Box margin="large 0 0 0" direction="column">
                <MissingDataBanner
                  text="Couldn't load policies. Please try to refresh or come back later."
                  refreshHandler={refetch}
                  refreshLoading={refetching}
                />
              </Box>
            )}

            <Card variant="secondary" margin="large 0" contentGap="large">
              <ComboBox<PolicyTypeOption, PolicyType>
                label="Policy type"
                value={policyType}
                items={policyTypeOptions}
                onChange={setPolicyType}
                isLoading={loading}
                tooltipInfo={
                  <>
                    <TooltipModalTitle>Policy type</TooltipModalTitle>
                    <TooltipModalBody align="start">
                      <Typography tag="p" variant="p-body3">
                        Spacelift uses an open-source project called{" "}
                        <TextLink href="https://www.openpolicyagent.org/">
                          <Typography tag="span" variant="p-body3">
                            Open Policy Agent
                          </Typography>
                        </TextLink>{" "}
                        and its rule language,{" "}
                        <TextLink href="https://www.openpolicyagent.org/docs/latest/policy-language/">
                          <Typography tag="span" variant="p-body3">
                            Rego
                          </Typography>
                        </TextLink>
                        , to execute user-defined pieces of code we call Policies at various
                        decision points. Policies come in different flavors that we call types, with
                        each type being executed at a different decision point.
                      </Typography>
                      <ReadMoreDocsLink docsUrl={getDocsUrl("/concepts/policy")} />
                    </TooltipModalBody>
                  </>
                }
              >
                {(item) => <ComboBoxDefaultItem id={item.value} label={item.label} />}
              </ComboBox>
              <ComboBox
                label="Select policy"
                value={policy}
                items={attachablePolicyOptions}
                onChange={setPolicy}
                isLoading={loading}
                isDisabled={!policyType}
              >
                {(item) => <ComboBoxDefaultItem id={item.value} label={item.label} />}
              </ComboBox>
              <Box justify="end">
                <Button
                  variant="contrast"
                  onPress={handleAttach}
                  disabled={attachablePolicies.length === 0 || !policy || isAttachingPolicy}
                  loading={isAttachingPolicy}
                >
                  Attach
                </Button>
              </Box>
            </Card>

            <Box gap="large" direction="column">
              <NewStackManuallyAttachedPolicies items={attachedPolicies} />
              <NewStackAutoAttachedPolicies items={autoAttachedPolicies} />
            </Box>
          </>
        )}
      </FullScreenModalBody>
      <NewStackFooter
        loading={loading}
        documentationLink={getDocsUrl("/concepts/policy#attaching-policies")}
        documentationTitle="Attach policy"
        documentationBody={<Documentation />}
      />
    </>
  );
};

export default NewStackAttachPolicies;
